import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { WSMessage, WSMessageType, createWSMessage, parseWSMessage, UnreadCountUpdate, UnreadCountUpdateAll, SocialEvent } from '../types/websocket';
import { useMessageStore } from '../stores/messageStore';
import { useWsStore } from '../stores/wsStore';
import { getGlobalMessageQueue } from '../utils/MessageQueue';
import { getConversationId, normalizeMessageType } from '../types/message';
import { WS_URL } from '../common/MetaSetting';

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_BASE_DELAY = 2000;
const RECONNECT_MAX_DELAY = 30000;
const HEARTBEAT_INTERVAL = parseInt(process.env.REACT_APP_WS_HEARTBEAT_INTERVAL || '30000', 10);
const DATA_REQUEST_TIMEOUT = 30000;

type WsState = 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED';

type PendingRequest = {
  resolve: (data: any) => void;
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
  /** 断线排队时写入：连接恢复后重发本请求 */
  resend?: () => void;
};

type MessageCallback = (message: WSMessage) => void;
type TypingCallback = (userId: number, isTyping: boolean, conversationId?: string) => void;
type ConnectCallback = () => void;
type DisconnectCallback = () => void;

let ws: WebSocket | null = null;
let wsState: WsState = 'CLOSED';
let currentUserId: number | null = null;
let heartbeatTimer: NodeJS.Timeout | null = null;
let reconnectTimer: NodeJS.Timeout | null = null;
let reconnectAttempt = 0;
let pongReceived = true;
let missedHeartbeats = 0;
const MAX_MISSED_HEARTBEATS = 3;
const HEARTBEAT_PONG_TIMEOUT = Math.max(HEARTBEAT_INTERVAL / 2, 10000);
let heartbeatPongTimer: NodeJS.Timeout | null = null;

const pendingRequests = new Map<string, PendingRequest>();
const messageCallbacks = new Map<string, MessageCallback>();
const typingCallbacks = new Map<string, TypingCallback>();
const connectCallbacks = new Set<ConnectCallback>();
const disconnectCallbacks = new Set<DisconnectCallback>();

// 断线期间排队的幂等请求重发闭包，连接恢复后统一重放
type ResendFn = (() => void) & { __messageId?: string };
const offlineRequestQueue: ResendFn[] = [];

const stateListeners = new Set<() => void>();

function notifyStateListeners() {
  stateListeners.forEach(fn => fn());
}

function getStateSnapshot(): WsState {
  return wsState;
}

function subscribeState(listener: () => void): () => void {
  stateListeners.add(listener);
  return () => { stateListeners.delete(listener); };
}

function startHeartbeat() {
  stopHeartbeat();
  pongReceived = true;
  missedHeartbeats = 0;
  heartbeatTimer = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        if (missedHeartbeats >= MAX_MISSED_HEARTBEATS) {
          console.warn(`[WS] 连续 ${MAX_MISSED_HEARTBEATS} 次心跳无Pong响应，主动断开重连`);
          if (ws) {
            ws.close(4000, 'Heartbeat timeout');
          }
          return;
        }
        const pingTimestamp = Date.now();
        const msg = createWSMessage(WSMessageType.HEARTBEAT, { timestamp: pingTimestamp });
        ws.send(JSON.stringify(msg));
        missedHeartbeats++;
        pongReceived = false;

        if (heartbeatPongTimer) {
          clearTimeout(heartbeatPongTimer);
        }
        heartbeatPongTimer = setTimeout(() => {
          if (!pongReceived) {
            console.warn('[WS] 心跳Pong响应超时');
          }
        }, HEARTBEAT_PONG_TIMEOUT);
      } catch (e) {
        console.error('发送心跳失败:', e);
      }
    }
  }, HEARTBEAT_INTERVAL);
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  if (heartbeatPongTimer) {
    clearTimeout(heartbeatPongTimer);
    heartbeatPongTimer = null;
  }
  missedHeartbeats = 0;
  pongReceived = true;
}

function scheduleReconnect() {
  if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
    console.warn('WebSocket重连次数已达上限，等待网络恢复或回到前台后重置');
    return;
  }
  if (reconnectTimer) return;

  // 指数退避 + 随机抖动（jitter）：与 CRDT 通道对齐，
  // 避免服务端抖动时全端固定序列造成同步重连风暴
  const delay = Math.min(
    RECONNECT_BASE_DELAY * Math.pow(1.5, reconnectAttempt) + Math.random() * 1000,
    RECONNECT_MAX_DELAY
  );
  console.log(`WebSocket将在 ${Math.round(delay)}ms 后尝试第 ${reconnectAttempt + 1} 次重连`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectAttempt++;
    useWsStore.getState().setReconnectAttempt(reconnectAttempt);
    doConnect();
  }, delay);
}

// 网络恢复/回到前台时立即重置计数并重连（对照 CrdtWebSocketProvider.handleOnline），
// 否则手机锁屏恢复、Wi-Fi 切换或重连序列耗尽后 IM 将静默失联
const handleNetworkOnline = () => {
  if (currentUserId !== null && wsState !== 'OPEN') {
    cancelReconnect();
    doConnect();
  }
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && currentUserId !== null && wsState !== 'OPEN') {
    cancelReconnect();
    doConnect();
  }
};

let networkListenersAttached = false;

function attachNetworkListeners() {
  if (networkListenersAttached || typeof window === 'undefined') return;
  window.addEventListener('online', handleNetworkOnline);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  networkListenersAttached = true;
}

function detachNetworkListeners() {
  if (!networkListenersAttached || typeof window === 'undefined') return;
  window.removeEventListener('online', handleNetworkOnline);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  networkListenersAttached = false;
}

function cancelReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempt = 0;
  useWsStore.getState().setReconnectAttempt(0);
}

function isCurrentUserSender(senderId: string | number): boolean {
  if (!senderId) return false;
  return senderId === currentUserId || String(senderId) === String(currentUserId);
}

function handleIncomingMessage(event: MessageEvent) {
  try {
    const parsed = parseWSMessage(event.data);
    if (!parsed) {
      console.warn('[WS] parseWSMessage returned null for:', event.data?.substring(0, 200));
      return;
    }

    console.log('[WS] 收到消息 messageType=', parsed.messageType, 'messageId=', parsed.messageId);

    if (parsed.messageId) {
      const pending = pendingRequests.get(parsed.messageId);
      if (pending) {
        console.log('[WS] 匹配到pending请求 messageId=', parsed.messageId, 'messageType=', parsed.messageType);
        clearTimeout(pending.timeout);
        pendingRequests.delete(parsed.messageId);
        pending.resolve(parsed.messageContent);
      }
    }

    if (parsed.messageType === WSMessageType.CHAT || parsed.messageType === WSMessageType.GROUP_MESSAGE) {
      const chatMsg = parsed.messageContent;
      const conversationId = chatMsg.conversationId || getConversationId(chatMsg.senderId, chatMsg.receiverId);
      const isSelf = isCurrentUserSender(chatMsg.senderId);
      const currentConv = useMessageStore.getState().currentConversation;
      const isCurrentConv = conversationId === currentConv;

      const messageId = chatMsg.id || parsed.messageId || `msg-${Date.now()}`;
      const store = useMessageStore.getState();
      const existing = store.hasMessageId(conversationId, messageId);

      const msgType = normalizeMessageType(chatMsg.chatMessageType || chatMsg.messageType);

      if (existing) {
        store.updateMessage(conversationId, messageId, {
          status: 'DELIVERED',
          timestamp: chatMsg.timestamp || Date.now(),
        });
      } else {
        const hasServerTimestamp = typeof chatMsg.timestamp === 'number' && chatMsg.timestamp > 0;
        const msgTimestamp = hasServerTimestamp ? (chatMsg.timestamp as number) : Date.now();
        const watermark = useMessageStore.getState().getWatermark(conversationId);
        // 缺少服务端时间戳的消息不参与水位比较（本地时钟偏快会导致后续真实消息被丢弃），
        // 仅保留消息本身，杜绝本地时钟污染水位
        if (hasServerTimestamp && watermark > 0 && msgTimestamp <= watermark) {
          return;
        }

        const message: any = {
          id: messageId,
          conversationId,
          senderId: chatMsg.senderId,
          receiverId: chatMsg.receiverId,
          content: chatMsg.content,
          messageType: msgType,
          timestamp: msgTimestamp,
          status: 'DELIVERED' as const,
          isSelf,
          imageUrl: chatMsg.imageUrl,
          thumbnailBase64: chatMsg.thumbnailBase64,
          thumbnailUrl: chatMsg.thumbnailUrl,
          imageWidth: chatMsg.imageWidth,
          imageHeight: chatMsg.imageHeight,
          fileName: chatMsg.fileName,
          fileUrl: chatMsg.fileUrl,
          fileSize: chatMsg.fileSize,
          mimeType: chatMsg.mimeType,
        };

        useMessageStore.getState().addMessage(conversationId, message);

        if (!isSelf && !isCurrentConv) {
          const cur = useMessageStore.getState().unreadCount.get(conversationId) || 0;
          // 统一走版本化轨道（时间戳为版本），避免本地乐观值被更早的服务端全量推送覆盖回退
          useMessageStore.getState().syncUnreadCountWithVersion(conversationId, cur + 1, Date.now());
        }

        // 可见即已读：会话打开中收到新消息时自动标记已读。服务端清零未读后
        // 会向本账号全部在线会话推送权威 UNREAD_COUNT_UPDATE，实现多端同步；
        // 未读的本地递增仅作打开会话前的即时展示，最终以服务端推送为准。
        if (!isSelf && isCurrentConv) {
          wsSendRequest(WSMessageType.MARK_AS_READ, { conversationId }).catch(() => {});
        }
      }
    } else if (parsed.messageType === WSMessageType.TYPING) {
      const data = parsed.messageContent;
      typingCallbacks.forEach(cb => cb(data.userId, data.isTyping, data.conversationId));
    } else if (parsed.messageType === WSMessageType.MESSAGE_ACK) {
      const ack = parsed.messageContent;
      if (ack.tempMessageId && ack.messageId) {
        useMessageStore.getState().updateMessage(ack.conversationId, ack.tempMessageId, {
          id: ack.messageId,
          status: ack.status || 'DELIVERED',
          timestamp: ack.timestamp || Date.now(),
        });
      }
    } else if (parsed.messageType === WSMessageType.UNREAD_COUNT_UPDATE) {
      const content = parsed.messageContent;
      if ('unreadCounts' in content && 'totalCount' in content) {
        const data = content as UnreadCountUpdateAll;
        const store = useMessageStore.getState();
        const version = data.timestamp || Date.now();
        Object.entries(data.unreadCounts).forEach(([convId, count]) => {
          store.syncUnreadCountWithVersion(convId, count, version);
        });
      } else if ('conversationId' in content) {
        const update = content as UnreadCountUpdate;
        const version = update.timestamp || Date.now();
        useMessageStore.getState().syncUnreadCountWithVersion(update.conversationId, update.unreadCount, version);
      }
    } else if (parsed.messageType === WSMessageType.FRIEND_REQUEST
               || parsed.messageType === WSMessageType.FRIEND_STATUS_CHANGE
               || parsed.messageType === WSMessageType.GROUP_JOIN_REQUEST
               || parsed.messageType === WSMessageType.GROUP_JOIN_REQUEST_RESPONSE) {
      const socialEvent = parsed.messageContent as SocialEvent;
      console.log('[WS] 收到社交事件:', parsed.messageType, socialEvent.eventType, 'targetUserId=', socialEvent.targetUserId);
      useMessageStore.getState().addSocialEvent(socialEvent);
    }

    if (parsed.messageType === WSMessageType.HEARTBEAT) {
      pongReceived = true;
      missedHeartbeats = 0;
      if (heartbeatPongTimer) {
        clearTimeout(heartbeatPongTimer);
        heartbeatPongTimer = null;
      }
      const sentTimestamp = parsed.messageContent?.timestamp;
      if (sentTimestamp) {
        const latency = Date.now() - sentTimestamp;
        useWsStore.getState().setHeartbeatLatency(latency);
      }
    }

    messageCallbacks.forEach(cb => cb(parsed));
  } catch (e) {
    console.error('解析WebSocket消息失败:', e);
  }
}

function doConnect() {
  if (ws) {
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      return;
    }
  }

  wsState = 'CONNECTING';
  useWsStore.getState().setReadyState('CONNECTING');
  notifyStateListeners();

  try {
    ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      wsState = 'OPEN';
      useWsStore.getState().setReadyState('OPEN');
      useWsStore.getState().setLastConnectedAt(Date.now());
      useWsStore.getState().setReconnectAttempt(0);
      reconnectAttempt = 0;
      startHeartbeat();
      notifyStateListeners();

      // 重连补偿：重放断线期间排队的幂等请求，并主动拉取未读数对账
      // （会话列表/历史由 useIMDataProvider 的 onConnect 回调负责刷新）
      flushOfflineRequestQueue();
      wsSendRequest(WSMessageType.GET_UNREAD_COUNTS, {}, DATA_REQUEST_TIMEOUT)
        .catch((e) => console.warn('[WS] 重连后拉取未读数失败:', e));

      connectCallbacks.forEach(cb => cb());
    };

    ws.onclose = (event) => {
      wsState = 'CLOSED';
      useWsStore.getState().setReadyState('CLOSED');
      stopHeartbeat();
      notifyStateListeners();
      disconnectCallbacks.forEach(cb => cb());

      if (!event.wasClean) {
        scheduleReconnect();
      }
    };

    ws.onerror = () => {
      wsState = 'CLOSED';
      useWsStore.getState().setReadyState('CLOSED');
      stopHeartbeat();
      notifyStateListeners();
    };

    ws.onmessage = handleIncomingMessage;
  } catch (e) {
    console.error('WebSocket创建失败:', e);
    wsState = 'CLOSED';
    useWsStore.getState().setReadyState('CLOSED');
    notifyStateListeners();
    scheduleReconnect();
  }
}

function wsConnect(userId: number) {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  currentUserId = userId;
  cancelReconnect();
  attachNetworkListeners();
  doConnect();
}

function wsDisconnect() {
  cancelReconnect();
  stopHeartbeat();
  detachNetworkListeners();

  // 丢弃断线排队中尚未重放的请求（调用方各自的超时/错误兜底会触发）
  offlineRequestQueue.length = 0;

  if (ws) {
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    ws.onopen = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close(1000, 'Client disconnect');
    }
    ws = null;
  }

  wsState = 'CLOSED';
  useWsStore.getState().setReadyState('CLOSED');
  notifyStateListeners();
  disconnectCallbacks.forEach(cb => cb());
}

function wsSendMessage(message: WSMessage) {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    throw new Error(`WebSocket未连接，无法发送消息 (state=${wsState})`);
  }
  const formatted = createWSMessage(message.messageType, message.messageContent, message.messageId);
  ws.send(JSON.stringify(formatted));
}

function flushOfflineRequestQueue() {
  if (offlineRequestQueue.length === 0) return;
  const queued = offlineRequestQueue.splice(0, offlineRequestQueue.length);
  queued.forEach((resend) => resend());
}

function wsSendRequest(
  messageType: WSMessageType,
  messageContent: any,
  timeoutMs: number = 10000,
  options?: { queueIfOffline?: boolean }
): Promise<any> {
  return new Promise((resolve, reject) => {
    const queueIfOffline = options?.queueIfOffline ?? false;

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // 幂等类请求可选择断线排队：连接恢复后自动重放（05-5.5）
      if (queueIfOffline) {
        const messageId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const timeout = setTimeout(() => {
          const idx = offlineRequestQueue.findIndex((fn) => fn.__messageId === messageId);
          if (idx > -1) offlineRequestQueue.splice(idx, 1);
          pendingRequests.delete(messageId);
          reject(new Error(`请求超时(断线排队): messageType=${messageType}, messageId=${messageId}`));
        }, timeoutMs);

        const resend: ResendFn = () => {
          try {
            const msg = createWSMessage(messageType, messageContent, messageId);
            ws?.send(JSON.stringify(msg));
          } catch (e) {
            clearTimeout(timeout);
            pendingRequests.delete(messageId);
            reject(e as Error);
          }
        };
        resend.__messageId = messageId;

        pendingRequests.set(messageId, { resolve, reject, timeout, resend });
        offlineRequestQueue.push(resend);
        return;
      }

      reject(new Error(`WebSocket未连接 (state=${wsState})`));
      return;
    }

    const messageId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const timeout = setTimeout(() => {
      pendingRequests.delete(messageId);
      reject(new Error(`请求超时: messageType=${messageType}, messageId=${messageId}`));
    }, timeoutMs);

    pendingRequests.set(messageId, { resolve, reject, timeout });

    try {
      const msg = createWSMessage(messageType, messageContent, messageId);
      const jsonStr = JSON.stringify(msg);
      console.log('[WS] 发送请求 messageType=', messageType, 'messageId=', messageId, 'pendingCount=', pendingRequests.size);
      ws!.send(jsonStr);
    } catch (e) {
      clearTimeout(timeout);
      pendingRequests.delete(messageId);
      reject(e);
    }
  });
}

function wsSendTypingIndicator(isTyping: boolean, conversationId?: string) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  const msg = createWSMessage(WSMessageType.TYPING, {
    userId: currentUserId,
    isTyping,
    ...(conversationId ? { conversationId } : {}),
  });
  ws.send(JSON.stringify(msg));
}

function wsIsConnected(): boolean {
  return wsState === 'OPEN';
}

function wsIsConnecting(): boolean {
  return wsState === 'CONNECTING';
}

function addMessageCallback(cb: MessageCallback): () => void {
  const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  messageCallbacks.set(id, cb);
  return () => { messageCallbacks.delete(id); };
}

function addTypingCallback(cb: TypingCallback): () => void {
  const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  typingCallbacks.set(id, cb);
  return () => { typingCallbacks.delete(id); };
}

function addConnectCallback(cb: ConnectCallback): () => void {
  connectCallbacks.add(cb);
  return () => { connectCallbacks.delete(cb); };
}

function addDisconnectCallback(cb: DisconnectCallback): () => void {
  disconnectCallbacks.add(cb);
  return () => { disconnectCallbacks.delete(cb); };
}

export function useGlobalWebSocket() {
  const readyState = useSyncExternalStore(subscribeState, getStateSnapshot);
  // 真实的重连计数（此前返回写死的 0），供 UI 展示"重连中"状态
  const reconnectAttempts = useWsStore((s) => s.reconnectAttempt);

  const messageQueue = useMemo(() => getGlobalMessageQueue(), []);

  useEffect(() => {
    messageQueue.setSendMessageCallback(async (wsMessage: WSMessage) => {
      wsSendMessage(wsMessage);
    });
    messageQueue.setStatusUpdateCallback((messageId: string, status: string) => {
      const currentConv = useMessageStore.getState().currentConversation;
      if (currentConv) {
        useMessageStore.getState().updateMessageStatus(currentConv, messageId, status as any);
      }
    });
  }, [messageQueue]);

  const connect = useCallback((userId: number) => {
    wsConnect(userId);
  }, []);

  const disconnect = useCallback(() => {
    wsDisconnect();
  }, []);

  const sendMessage = useCallback((message: WSMessage) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      wsSendMessage(message);
    } else {
      messageQueue.enqueue(message);
    }
  }, [messageQueue]);

  const sendRequest = useCallback((messageType: WSMessageType, messageContent: any, timeoutMs?: number) => {
    return wsSendRequest(messageType, messageContent, timeoutMs);
  }, []);

  // 以下为幂等类请求：断线时排队，重连后自动重放（非幂等请求保持原样立即失败）
  const getPrivateConversationsViaWS = useCallback((page: number = 1, size: number = 100) => {
    return wsSendRequest(WSMessageType.GET_PRIVATE_CONVERSATIONS, { page, size }, DATA_REQUEST_TIMEOUT, { queueIfOffline: true });
  }, []);

  const getGroupConversationsViaWS = useCallback((page: number = 1, size: number = 100) => {
    return wsSendRequest(WSMessageType.GET_GROUP_CONVERSATIONS, { page, size }, DATA_REQUEST_TIMEOUT, { queueIfOffline: true });
  }, []);

  const getChatHistoryViaWS = useCallback((conversationId: string, page: number = 1, size: number = 50) => {
    return wsSendRequest(WSMessageType.GET_CHAT_HISTORY, { conversationId, page, size }, DATA_REQUEST_TIMEOUT, { queueIfOffline: true });
  }, []);

  const getChatHistoryByCursorViaWS = useCallback((conversationId: string, beforeMessageId?: string, size: number = 50) => {
    const content: any = { conversationId, size };
    if (beforeMessageId) {
      content.beforeMessageId = beforeMessageId;
    }
    return wsSendRequest(WSMessageType.GET_CHAT_HISTORY, content, DATA_REQUEST_TIMEOUT, { queueIfOffline: true });
  }, []);

  const markAsReadViaWS = useCallback((conversationId: string) => {
    return wsSendRequest(WSMessageType.MARK_AS_READ, { conversationId }, undefined, { queueIfOffline: true });
  }, []);

  const createPrivateConversationViaWS = useCallback((friendId: string) => {
    return wsSendRequest(WSMessageType.CREATE_PRIVATE_CONVERSATION, { friendId });
  }, []);

  const createGroupConversationViaWS = useCallback((groupId: number, groupName: string, groupAvatarPath?: string) => {
    const content: any = { groupId, groupName };
    if (groupAvatarPath) content.groupAvatarPath = groupAvatarPath;
    return wsSendRequest(WSMessageType.CREATE_GROUP_CONVERSATION, content);
  }, []);

  const getUnreadCountsViaWS = useCallback(() => {
    return wsSendRequest(WSMessageType.GET_UNREAD_COUNTS, {}, DATA_REQUEST_TIMEOUT, { queueIfOffline: true });
  }, []);

  const sendTypingIndicator = useCallback((isTyping: boolean, conversationId?: string) => {
    wsSendTypingIndicator(isTyping, conversationId);
  }, []);

  const isConnected = useCallback(() => wsIsConnected(), []);
  const isConnecting = useCallback(() => wsIsConnecting(), []);

  const onMessage = useCallback((cb: MessageCallback) => {
    return addMessageCallback(cb);
  }, []);

  const onConnect = useCallback((cb: ConnectCallback) => {
    return addConnectCallback(cb);
  }, []);

  const onDisconnect = useCallback((cb: DisconnectCallback) => {
    return addDisconnectCallback(cb);
  }, []);

  const onTyping = useCallback((cb: TypingCallback) => {
    return addTypingCallback(cb);
  }, []);

  return {
    connect,
    disconnect,
    sendMessage,
    sendRequest,
    getPrivateConversationsViaWS,
    getGroupConversationsViaWS,
    getChatHistoryViaWS,
    getChatHistoryByCursorViaWS,
    markAsReadViaWS,
    createPrivateConversationViaWS,
    createGroupConversationViaWS,
    getUnreadCountsViaWS,
    isConnected,
    isConnecting,
    readyState,
    reconnectAttempts,
    onMessage,
    onConnect,
    onDisconnect,
    onTyping,
    sendTypingIndicator,
    lastMessage: null,
    messageQueue,
  };
}
